import { PopoverOrigin } from '@material-ui/core/Popover';
import { isArray } from '@pe-libs/react-components';
import PeFlag from '@pe-libs/react-components/build/components/PeFlag';
import HelpCircleOutlined from '@pe-libs/react-components/build/components/PeIcons/HelpCircleOutlined.svg';
import PeMenu from '@pe-libs/react-components/build/components/PeMenu';
import useIntlMessage from '@pe-libs/react-components/build/lib/intl/hooks/useIntlMessage';
import { useRouter } from 'next/router';
import React, { FunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import { getUserLocale } from '#modules/app/selectors';
import classNames from '#utils/classNames';
import useStyles from '../../modules/auth/components/PeContentLayout/styles';
import messages from './messages';
import { LangSwitchProps } from './types';

const LanguageSwitch: FunctionComponent<LangSwitchProps> = ({ isHoverable }) => {
  const styles = useStyles({
    avatarUrl: '',
  });
  const classesNavIcon = classNames(styles.icon, styles.iconLangSwitch);
  const anchorOrigin: PopoverOrigin = {
    vertical: 'bottom',
    horizontal: 'right',
  };
  const triggerButton = <HelpCircleOutlined />;
  const selectedId = useSelector(getUserLocale)?.toLowerCase();
  const formatMessage = useIntlMessage();
  const router = useRouter();

  const queryLang = (isArray(router?.query.lang) ? router.query.lang[0] : router?.query.lang) ?? 'de-de';
  const langCode = queryLang.slice(3);

  const path = router?.asPath?.replace(`/${queryLang}/`, '');

  const registrationProcess = path?.includes('register') || path?.includes('onboarding');

  const langOptions = [
    {
      id: 'de-de',
      title: formatMessage(messages.languageGerman),
      altTitle: 'DE',
      onMenuClick: () => {
        router.push({ pathname: router.pathname, query: { ...router.query, lang: 'de-de' } }, `/de-de/${path}`);
      },
      icon: <PeFlag code="de" className={styles.flag} />,
    },
    {
      id: 'en-us',
      title: formatMessage(messages.languageEnglishUs),
      altTitle: 'US',
      onMenuClick: () => {
        router.push({ pathname: router.pathname, query: { ...router.query, lang: 'en-us' } }, `/en-us/${path}`);
      },
      icon: <PeFlag code="us" className={styles.flag} />,
    },
    {
      id: 'en-gb',
      title: formatMessage(messages.languageEnglishGb),
      altTitle: 'GB',
      onMenuClick: () => {
        router.push({ pathname: router.pathname, query: { ...router.query, lang: 'en-gb' } }, `/en-gb/${path}`);
      },
      icon: <PeFlag code="gb" className={styles.flag} />,
    },
    {
      id: 'es-es',
      title: formatMessage(messages.languageSpanish),
      altTitle: 'ES',
      onMenuClick: () => {
        router.push({ pathname: router.pathname, query: { ...router.query, lang: 'es-es' } }, `/es-es/${path}`);
      },
      icon: <PeFlag code="es" className={styles.flag} />,
    },
    {
      id: 'fr-fr',
      title: formatMessage(messages.languageFrench),
      altTitle: 'FR',
      onMenuClick: () => {
        router.push({ pathname: router.pathname, query: { ...router.query, lang: 'fr-fr' } }, `/fr-fr/${path}`);
      },
      icon: <PeFlag code="fr" className={styles.flag} />,
    },
    {
      id: 'it-it',
      title: formatMessage(messages.languageItalian),
      altTitle: 'IT',
      onMenuClick: () => {
        router.push({ pathname: router.pathname, query: { ...router.query, lang: 'it-it' } }, `/it-it/${path}`);
      },
      icon: <PeFlag code="it" className={styles.flag} />,
    },
    {
      id: 'pt-pt',
      title: formatMessage(messages.languagePortuguese),
      altTitle: 'PT',
      onMenuClick: () => {
        router.push({ pathname: router.pathname, query: { ...router.query, lang: 'pt-pt' } }, `/pt-pt/${path}`);
      },
      icon: <PeFlag code="pt" className={styles.flag} />,
    },
    {
      id: 'nl-nl',
      title: formatMessage(messages.languageDutch),
      altTitle: 'NL',
      onMenuClick: () => {
        router.push({ pathname: router.pathname, query: { ...router.query, lang: 'nl-nl' } }, `/nl-nl/${path}`);
      },
      icon: <PeFlag code="nl" className={styles.flag} />,
    },
    {
      id: 'pl-pl',
      title: formatMessage(messages.languagePolish),
      altTitle: 'PL',
      onMenuClick: () => {
        router.push({ pathname: router.pathname, query: { ...router.query, lang: 'pl-pl' } }, `/pl-pl/${path}`);
      },
      icon: <PeFlag code="pl" className={styles.flag} />,
    },
    {
      id: 'tr-tr',
      title: formatMessage(messages.languageTurkish),
      altTitle: 'TR',
      onMenuClick: () => {
        router.push({ pathname: router.pathname, query: { ...router.query, lang: 'tr-tr' } }, `/tr-tr/${path}`);
      },
      icon: <PeFlag code="tr" className={styles.flag} />,
    },
    {
      id: 'et-ee',
      title: formatMessage(messages.languageEstonian),
      altTitle: 'ET',
      onMenuClick: () => {
        router.push({ pathname: router.pathname, query: { ...router.query, lang: 'et-ee' } }, `/et-ee/${path}`);
      },
      icon: <PeFlag code="ee" className={styles.flag} />,
    },
    {
      id: 'fi-fi',
      title: formatMessage(messages.languageFinnish),
      altTitle: 'FI',
      onMenuClick: () => {
        router.push({ pathname: router.pathname, query: { ...router.query, lang: 'fi-fi' } }, `/fi-fi/${path}`);
      },
      icon: <PeFlag code="fi" className={styles.flag} />,
    },
    {
      id: 'sv-se',
      title: formatMessage(messages.languageSwedish),
      altTitle: 'SV',
      onMenuClick: () => {
        router.push({ pathname: router.pathname, query: { ...router.query, lang: 'sv-se' } }, `/sv-se/${path}`);
      },
      icon: <PeFlag code="se" className={styles.flag} />,
    },
    {
      id: 'da-dk',
      title: formatMessage(messages.languageDanish),
      altTitle: 'DA',
      onMenuClick: () => {
        router.push({ pathname: router.pathname, query: { ...router.query, lang: 'da-dk' } }, `/da-dk/${path}`);
      },
      icon: <PeFlag code="dk" className={styles.flag} />,
    },
    {
      id: 'cs-cz',
      title: formatMessage(messages.languageCzech),
      altTitle: 'CS',
      onMenuClick: () => {
        router.push({ pathname: router.pathname, query: { ...router.query, lang: 'cs-cz' } }, `/cs-cz/${path}`);
      },
      icon: <PeFlag code="cz" className={styles.flag} />,
    },
  ];

  const enabledLanguages = registrationProcess
    ? langOptions
    : langOptions.filter((option) => {
      switch (option.id) {
        case 'it-it': {
          return ['it-it'].includes(queryLang);
        }

        case 'fr-fr': {
          return ['fr-fr'].includes(queryLang);
        }

        case 'es-es': {
          return ['es-es'].includes(queryLang);
        }

        case 'pt-pt': {
          return ['pt-pt'].includes(queryLang);
        }

        case 'nl-nl': {
          return ['nl-nl'].includes(queryLang);
        }

        case 'pl-pl': {
          return ['pl-pl'].includes(queryLang);
        }

        case 'tr-tr': {
          return ['tr-tr'].includes(queryLang);
        }

        case 'da-dk': {
          return ['da-dk'].includes(queryLang);
        }

        case 'cs-cz': {
          return ['cs-cz'].includes(queryLang);
        }

        case 'sv-se': {
          return ['sv-se'].includes(queryLang);
        }

        case 'fi-fi': {
          return ['fi-fi'].includes(queryLang);
        }

        case 'et-ee': {
          return ['et-ee'].includes(queryLang);
        }

        case 'de-de': {
          return ['de-de', 'en-us', 'en-gb'].includes(queryLang) || !queryLang;
        }

        default: {
          return true;
        }
      }
    });

  return (
    <div className={classesNavIcon}>
      <PeMenu
        variant="selectedMenu"
        items={enabledLanguages}
        buttonElement={<PeFlag code={langCode} className={styles.flagButton} />}
        stylesMenuButton={styles.langSwitch}
        triggerBtn={triggerButton}
        anchorOrigin={anchorOrigin}
        stylesPopoverMenu={styles.langMenu}
        selectedById={selectedId}
        isHoverable={isHoverable}
        transitionDuration={300}
        hideTriggerButtonText={true}
      />
    </div>
  );
};

export default LanguageSwitch;
